import {
	AfterViewInit,
	Component,
	Input,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { rememberAddPropertyCompanyId } from 'src/app/ui/add-property/add-property-main-tab.component';
import { mergeMap } from 'rxjs/operators';
import {
	DataSetComponent,
	DropdownOption,
} from 'src/app/components/data-set/data-set.component';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { GeoService } from 'src/app/services/geo.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import Property = Components.Schemas.Location;
import { MarkerInfo } from 'src/app/components/data-set/data-set-map.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPresentationToggleComponent,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import {
	DataSetTableComponent,
	DataSetRowClickDirective,
} from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgClass } from '@angular/common';
import { DataSetMapViewComponent } from '../../../components/data-set/data-set-map.component';
import { AddressPipe } from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

export type PropertyDataRequest = (
	filter: QueryParam[]
) => Promise<{ property: Property; deviceCount: number }[]>;

@Component({
	selector: 'app-property-list-table',
	templateUrl: './property-list-table.component.html',
	styleUrls: ['./property-list-table.component.scss', '../../common/main.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		DataSetPresentationToggleComponent,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatChipSet,
		MatChip,
		NgClass,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetRowClickDirective,
		DataSetMapViewComponent,
		AddressPipe,
		DataSetLoadingRowComponent,
	],
})
export class PropertyListTableComponent implements OnInit, AfterViewInit {
	@Input() isUnitTesting = false; // Do NOT set this variable to true! Only used for unit tests.
	@Input() hideToolbar = false; // Hide search/filter toolbar (used for single property display)
	@Input() propertyDataRequest?: PropertyDataRequest; // Optional callback to provide custom property data fetching
	@Input() hidePendingInstalls = false; // Hide the Pending Installs column
	@Input() swapDevicesAndStatus = false; // Swap the order of Devices and Status columns
	/** When set (including false), overrides the default Property Management Add check. */
	@Input() canAddProperty?: boolean;
	/** Company id passed to /add-property; defaults to the caller's active company. */
	@Input() addTargetCompanyId?: string;
	/** When set, row clicks stay in the company shell at /company/:id/detail/property/:id/detail. */
	@Input() companyShellCompanyId?: string;
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	displayedColumns = [
		'propertyName',
		'address',
		'devices',
		'active',
		'pendingInstalls',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<{
		property: Property;
		deviceCount: number;
	}>();
	activeCompanyId: string | undefined = '';
	private defaultCanAddProperty: boolean;
	localizePropertyName = $localize`:@@propertyListTable-propertyNameLabel:Property Name`;
	markersInfo: MarkerInfo[] = [];

	get canEditProperties(): boolean {
		return this.canAddProperty ?? this.defaultCanAddProperty;
	}

	filterLabels = {
		defaultPlaceholder: $localize`:@@propertyListTable-defaultPlaceholder:Search property name...`,
		propertyName: $localize`:@@propertyListTable-filterLabelPropertyNameLabel:Property Name`,
		status: $localize`:@@propertyListTable-filterLabelStatusLabel:Status`,
		hasPendingInstalls: $localize`:@@propertyListTable-filterLabelHasPendingInstalls:Pending Installs`,
		streetAddress: $localize`:@@propertyListTable-filterLabelStreetAddressLabel:Street Address`,
		townCity: $localize`:@@propertyListTable-filterLabelTownCityLabel:Town / City`,
		stateProvince: $localize`:@@propertyListTable-filterLabelStateProvinceLabel:State / Province`,
		postalCode: $localize`:@@propertyListTable-filterLabelPostalCodeLabel:Postal Code`,
		country: $localize`:@@propertyListTable-filterLabelCountryLabel:Country`,
	};

	activeInactiveDropdownOptions: DropdownOption[] = [
		{ label: $localize`:@@propertyListTable-active:Active`, value: 'true' },
		{
			label: $localize`:@@propertyListTable-inactive:Inactive`,
			value: 'false',
		},
	];

	yesNoDropdownOptions: DropdownOption[] = [
		{
			label: $localize`:@@propertyListTable-hasPendingInstallsYes:Yes`,
			value: 'true',
		},
		{
			label: $localize`:@@propertyListTable-hasPendingInstallsNo:No`,
			value: 'false',
		},
	];
	countryDropdownOptions: DropdownOption[];

	getPropertiesLoadingKey = ApiLoadingKeys.getProperties;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly scService: SystemCapabilityService,
		private readonly geoService: GeoService,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.countryDropdownOptions = this.geoService.allCountries.map(
			(country) => ({
				label: country.name,
				value: country.twoLetterCode,
			})
		);
		this.defaultCanAddProperty = this.scService.has(
			SystemCapabilityEnum.propertyManagement
		);
	}

	async ngOnInit() {
		await this.setActiveCompanyId();

		// Update displayed columns based on flags
		if (this.swapDevicesAndStatus) {
			// Swap devices and status columns: status comes before devices
			const devicesIndex = this.displayedColumns.indexOf('devices');
			const activeIndex = this.displayedColumns.indexOf('active');
			if (devicesIndex !== -1 && activeIndex !== -1) {
				this.displayedColumns[devicesIndex] = 'active';
				this.displayedColumns[activeIndex] = 'devices';
			}
		}

		if (this.hidePendingInstalls) {
			this.displayedColumns = this.displayedColumns.filter(
				(col) => col !== 'pendingInstalls'
			);
		}
	}

	ngAfterViewInit(): void {
		// If propertyDataRequest is provided, use it; otherwise load all properties with filtering
		if (this.propertyDataRequest) {
			this.dataSet.queryParams
				.pipe(
					mergeMap(async (filter) => {
						const result = await this.propertyDataRequest!(filter);
						this.finishedLoadingInitData = true;
						return result;
					})
				)
				.subscribe((response) => {
					this.dataSource.data = response;
					this.setMapMarkers();
				});
		} else {
			// Default behavior: load all properties with filtering
			this.dataSet.queryParams
				.pipe(
					mergeMap(async (filter) => {
						const result = await this.apiService.getProperties(filter);
						this.finishedLoadingInitData = true;
						return result;
					})
				)
				.subscribe((response) => {
					this.dataSource.data = response;
					this.setMapMarkers();
				});
		}
	}

	onAddButtonClick(): void {
		const companyId = this.addTargetCompanyId ?? this.activeCompanyId;
		rememberAddPropertyCompanyId(companyId);

		this.router.navigateByUrl('/add-property', {
			state: { companyId },
		});
	}

	onRowClick(property: Property): void {
		if (this.companyShellCompanyId) {
			// Preserve tabIndex so company shell keeps Properties selected.
			this.router.navigate(
				[
					`/company/${this.companyShellCompanyId}/detail/property/${property.id}/detail`,
				],
				{ queryParamsHandling: 'preserve' }
			);
			return;
		}
		this.router.navigate(['/property-details', property.id]);
	}

	async setActiveCompanyId(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id;
	}

	setMapMarkers(): MarkerInfo[] {
		this.markersInfo = [];
		this.dataSource.data.forEach(({ property }: any) => {
			if (property.address.geoCoordinates) {
				const position = {
					lat: property.address.geoCoordinates?.latitude,
					lng: property.address.geoCoordinates?.longitude,
				};

				const locationName = property.name;
				const address1 = property.address.address1 ?? '';
				const locality = property.address.locality ?? '';
				const postalCode = property.address.postalCode ?? '';
				const locationId = property.id ?? '';

				const title =
					locationName + '\n' + address1 + '\n' + locality + ' ' + postalCode;

				const marker = {
					position,
					icon: '',
					title,
					clickable: true,
				};

				this.markersInfo.push({
					marker,
					deviceId: '',
					deviceSn: '',
					locationName,
					address1,
					locality,
					postalCode,
					locationId,
				});
			}
		});

		return this.markersInfo;
	}

	rowId(row: { property: Property; deviceCount: number }): string {
		return row?.property?.id || 'unknown-id';
	}
}
